import React from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const tree = (
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>
);

const rootElement = document.getElementById("root");

// Differentiate between two possible initial DOM states inside #root:
//
//   (A) Static SEO fallback shell (production index.html):
//       <div id="root"><div data-seo-fallback="true">…h1, p, nav…</div></div>
//       → use createRoot so React cleanly REPLACES the fallback. No
//         hydration mismatch is reported because we cleared #root first.
//
//   (B) Prerendered React snapshot (build/<route>/index.html):
//       <div id="root"><div class="App">…live React markup…</div></div>
//       → use hydrateRoot so the prerendered markup is preserved and
//         simply re-attached to React's virtual DOM. Helmet metadata
//         that was already baked into <head> by the prerender pipeline
//         stays correct for crawlers.
//
//   (C) Empty shell (dev mode `yarn start`):
//       <div id="root"></div>
//       → use createRoot (default CRA behaviour).
const firstChild = rootElement.firstElementChild;
const isFallbackShell =
  firstChild && firstChild.getAttribute("data-seo-fallback") === "true";

if (isFallbackShell) {
  // Clear the SEO fallback so React's createRoot starts from a blank slate.
  // Crawlers without JS already consumed it; users with JS see the real app.
  // Using replaceChildren() avoids invoking the HTML parser (safer than innerHTML="").
  rootElement.replaceChildren();
  createRoot(rootElement).render(tree);
} else if (rootElement.hasChildNodes()) {
  hydrateRoot(rootElement, tree);
} else {
  createRoot(rootElement).render(tree);
}
